import React from 'react';
import { useGraphql } from '../../../v2/hooks/useGraphql';
import type { CmsDataProps } from './CmsDataContext';

/**
 * Config structure for data fetching from CMS
 *
 * @example
 * {
 *   "query": "query GetKpiData($programId: Int!) { ... }",
 *   "variables": {
 *     "programId": "{{selectedProgram}}",
 *     "limit": 10
 *   },
 *   "fetchPolicy": "cache-first"
 * }
 */
export interface DataFetchConfig {
  /** GraphQL query string */
  query: string;
  /** Variables with {{filterKey}} placeholders for Redux filters */
  variables?: Record<string, unknown>;
  /** Apollo fetch policy */
  fetchPolicy?:
    | 'cache-first'
    | 'network-only'
    | 'cache-only'
    | 'no-cache'
    | 'standby';
  /** Whether the query is enabled and should execute (default: true) */
  enabled?: boolean;
}

interface DataFetchingWrapperProps {
  /** Data fetching configuration from CMS */
  config: DataFetchConfig;
  /** Children render function that receives data props */
  children: (dataProps: CmsDataProps) => React.ReactNode;
}

/**
 * DataFetchingWrapper - Wraps CMS components with data fetching capability
 *
 * When a CMS component has a `config` field with data fetching configuration,
 * this wrapper:
 * 1. Uses useGraphql hook to fetch data based on the config
 * 2. Passes all state (data, isLoading, error) to children via render props
 * 3. Children are responsible for handling loading/error UI
 *
 * Children receive data via props (render prop pattern):
 * - data: The fetched GraphQL data (null while loading or on error)
 * - isLoading: Loading state
 * - error: Error array (empty if no errors)
 * - refetch: Function to manually refetch
 */
const DataFetchingWrapper: React.FC<DataFetchingWrapperProps> = ({
  config,
  children,
}) => {
  const { data, isLoading, error, refetch } = useGraphql({
    query: config.query,
    variables: config.variables ?? {},
    fetchPolicy: config.fetchPolicy ?? 'cache-first',
    enabled: config.enabled !== false,
  });

  // Always pass state to children - let them handle loading/error UI
  // This allows components like Card to show spinner in body while keeping header
  return <>{children({ data, isLoading, error: error ?? [], refetch })}</>;
};

export default DataFetchingWrapper;
